import {
  Checkbox,
  Form,
  InlineLoading,
  InlineNotification,
  TextInput,
} from '@carbon/react';
import classNames from 'classnames';
import { EmptyState } from 'components/empty-state';
import { DropdownField } from 'components/form-fields/dropdown-field';
import { SplitPanel } from 'components/split-panel';
import { NumberField } from 'components/form-fields/number-field';
import { TextAreaField } from 'components/form-fields/text-area-field';
import { TextField } from 'components/form-fields/text-field';
import { QuarterCalls } from 'components/quarter-calls';
import { RelatedItemsSection } from 'components/related-items';
import { SurveyAreaAutocomplete } from 'components/survey-area-autocomplete';
import NoMapSvg from 'images/srp-illustration-no-map.svg';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import {
  crmTractFormAtom,
  crmTractFormLoadableAtom,
  createdTractDetailsLoadableAtom,
  errorAtom,
  isMapVisibleLoadableAtom,
  tractEnumLoadableAtom,
  validateNetAcresAtom,
} from './atoms';
import {
  getDefaultRoyaltyRateForInstrumentType,
  interestsCalculatorConfigAtom,
  interestsFor,
} from './interest-calculation-atoms';
import { InterestConversionSection } from './interest-conversion-section';
import { LotTags } from './lot-tags';
import style from './tract-creation.module.scss';
import { RuledField, TitleQuality, TitleRiskFactor } from './types';
import { useInterestCalculation } from './use-interest-calculation';
import {
  allowedValuesFor,
  fixedNumber,
  fixedPercentHint,
  isLockedField,
  isUnusedField,
  titleQualityItems,
  titleRiskFactorItems,
} from './utils';
import { SurveyAreaMap } from './tract-map';

type TractForm = Awaited<ReturnType<typeof crmTractFormAtom.read>>;

const titleQualityLabel = (item: TitleQuality) => {
  const labels: Record<TitleQuality, string> = {
    none: 'None',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  };
  return labels[item] || item;
};

const titleRiskFactorLabel = (item: TitleRiskFactor) => {
  const labels: Record<TitleRiskFactor, string> = {
    none: 'None',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
  };
  return labels[item] || item;
};

const TractCreationContent = ({ form }: { form: TractForm }) => {
  const tractDetailsLoadable = useAtomValue(createdTractDetailsLoadableAtom);
  const tract =
    tractDetailsLoadable.state === 'hasData' ? tractDetailsLoadable.data : null;
  const tractName = tract?.name ?? null;

  const isMapVisibleLoadable = useAtomValue(isMapVisibleLoadableAtom);
  const isMapVisible =
    isMapVisibleLoadable.state === 'hasData'
      ? isMapVisibleLoadable.data
      : false;
  const tractEnums = useAtomValue(tractEnumLoadableAtom);
  const [referenceLocationField, setReferenceLocationField] = useAtom(
    form.referenceLocation
  );
  const validateNetAcres = useSetAtom(validateNetAcresAtom);
  const error = useAtomValue(errorAtom);

  // Interests calculator configuration (guaranteed to be loaded by crmTractFormAtom)
  const interestsConfig = useAtomValue(interestsCalculatorConfigAtom);
  const instrumentType = useAtomValue(form.instrumentType);
  const tractSource = useAtomValue(form.tractSource);
  const [quarterCalls, setQuarterCalls] = useAtom(form.quarterCalls);
  const [gridException, setGridException] = useAtom(form.gridException);
  const [lots, setLots] = useAtom(form.lots);
  const grossAcres = useAtomValue(form.grossAcres);
  const tractNetAcres = useAtomValue(form.tractNetAcres);
  const [netAcreageUnits, setNetAcreageUnits] = useAtom(form.netAcreageUnits);
  const [leaseRoyaltyRate, setLeaseRoyaltyRate] = useAtom(
    form.leaseRoyaltyRate
  );
  const royaltyBurdens = useAtomValue(form.royaltyBurdens);
  const orriBurdens = useAtomValue(form.orriBurdens);

  // The server's rules for the chosen interest type's fields (GET /crm/enums).
  const allowed = useMemo(
    () =>
      allowedValuesFor(
        tractEnums.state === 'hasData'
          ? tractEnums.data.instrumentType
          : undefined,
        instrumentType.value
      ),
    [tractEnums, instrumentType.value]
  );
  const fixedLeaseRoyaltyRate = fixedNumber(allowed, 'leaseRoyaltyRate');
  const allowedUnits = allowed?.netAcreageUnits;

  // A rate the interest type fixes (UMI's 100%) is set; otherwise the type's
  // default fills an empty rate, unless the type carries no rate at all.
  // Nothing is set until the rules for the type are known.
  useEffect(() => {
    const type = instrumentType.value;
    if (!type || !allowed || isUnusedField(allowed, 'leaseRoyaltyRate')) return;

    if (fixedLeaseRoyaltyRate !== undefined) {
      const percent = fixedLeaseRoyaltyRate * 100;
      if (leaseRoyaltyRate.value !== percent) {
        setLeaseRoyaltyRate(percent);
      }
      return;
    }

    if (leaseRoyaltyRate.value === null) {
      const defaultRate = getDefaultRoyaltyRateForInstrumentType(
        type,
        interestsConfig
      );
      if (defaultRate !== null) {
        setLeaseRoyaltyRate(defaultRate * 100);
      }
    }
  }, [
    instrumentType.value,
    allowed,
    fixedLeaseRoyaltyRate,
    leaseRoyaltyRate.value,
    setLeaseRoyaltyRate,
    interestsConfig,
  ]);

  // Once a type is chosen or changed, the units follow it: a type that takes
  // one unit (NMA for OGL and UMI, NA for Surface, Surface Lease and
  // Commission) has it set, as a rate the type fixes is, and any other unit
  // the type does not allow is cleared. An opened tract keeps its stored
  // units, which the server checks on an edit only when the type or units
  // change, so a save never relabels its acreage unseen.
  const onlyUnit =
    allowedUnits?.length === 1 ? String(allowedUnits[0]) : undefined;
  useEffect(() => {
    if (!allowedUnits || !instrumentType.isDirty) return;
    const currentUnits = netAcreageUnits.value;
    if (onlyUnit !== undefined) {
      if (currentUnits !== onlyUnit) setNetAcreageUnits(onlyUnit);
    } else if (currentUnits && !allowedUnits.includes(currentUnits)) {
      setNetAcreageUnits('');
    }
  }, [
    allowedUnits,
    onlyUnit,
    instrumentType.isDirty,
    netAcreageUnits.value,
    setNetAcreageUnits,
  ]);

  const instrumentTypeItems = useMemo(() => {
    if (tractEnums.state !== 'hasData') return [];
    return tractEnums.data.instrumentType.map((item) => item.value);
  }, [tractEnums]);

  const tractSourceItems = useMemo(() => {
    if (tractEnums.state !== 'hasData') return [];
    return tractEnums.data.tractSource.map((item) => item.value);
  }, [tractEnums]);

  // Only the units the type allows are offered; stored units it does not
  // allow stay listed until they are changed.
  const netAcreageUnitsItems = useMemo(() => {
    if (tractEnums.state !== 'hasData') return [];
    const allUnits = tractEnums.data.netAcreageUnit.map((item) => item.value);
    return allowedUnits
      ? allUnits.filter(
          (unit) =>
            allowedUnits.includes(unit) || unit === netAcreageUnits.value
        )
      : allUnits;
  }, [tractEnums, allowedUnits, netAcreageUnits.value]);

  const royaltyTypeItems = useMemo(() => {
    if (tractEnums.state !== 'hasData') return [];
    return tractEnums.data.royaltyType.map((item) => item.value);
  }, [tractEnums]);

  // The interest preview runs for the types the ownership calculation covers.
  const isBundleOfSticksSupported = useMemo(
    () => !!interestsFor(interestsConfig, instrumentType.value),
    [instrumentType.value, interestsConfig]
  );

  /**
   * A field the interest type does not carry is disabled, and every one is
   * until a type the server lists is chosen (it drops all four for any other
   * type); so is a rate the type fixes.
   * @param {RuledField} field - The field key
   * @return {boolean} Whether the field is disabled
   */
  const isFieldDisabled = (field: RuledField) =>
    !allowed || isLockedField(allowed, field);

  const geographies = useMemo(() => {
    if (!referenceLocationField?.value) return [];
    return [
      {
        id: referenceLocationField?.value?.id,
        referenceLocation: referenceLocationField?.value,
        quarterCalls: quarterCalls,
      },
    ];
  }, [referenceLocationField, quarterCalls]);

  // Interest calculation hook - only enabled for supported instrument types
  const { interestConversionData, isLoading: isInterestCalculationLoading } =
    useInterestCalculation({
      formFields: {
        instrumentType,
        grossAcres,
        tractNetAcres,
        netAcreageUnits,
        leaseRoyaltyRate,
        royaltyBurdens,
        orriBurdens,
      },
      enabled: isBundleOfSticksSupported,
    });

  return (
    // The plain class is the anchor the slide-in Link panel pushes; CSS-module
    // names are hashed, so it can't be one of those.
    <div className={classNames(style.tractStepShell, 'crm-tract-wizard-shell')}>
      <SplitPanel
        left={
          <div className={style.tractCreationFormContainer}>
            <h2>{tractName ?? 'Tract*'}</h2>
            {error && (
              <InlineNotification
                hideCloseButton
                lowContrast
                kind="error"
                title="Error"
                subtitle={error}
              ></InlineNotification>
            )}
            <Form className={style.tractCreationForm}>
              <div className={style.formRow}>
                <div className={style.formItem}>
                  <DropdownField
                    data-testid="tract-creation-instrument-type"
                    light={true}
                    className={style.formItem}
                    label="Choose an option"
                    field={form.instrumentType}
                    id="instrument-type"
                    selectedItem={instrumentType.value}
                    titleText="Instrument Type*"
                    items={instrumentTypeItems}
                  ></DropdownField>
                </div>
                <div className={style.formItem}>
                  <DropdownField
                    data-testid="tract-creation-tract-source"
                    light={true}
                    className={style.formItem}
                    label="Choose an option"
                    field={form.tractSource}
                    id="tract-source"
                    selectedItem={tractSource.value}
                    titleText="Tract Source"
                    items={tractSourceItems}
                  ></DropdownField>
                </div>
              </div>
              <span className={style.surveyDescription}>
                Use survey area to define the starting point for the CRM Tract.
              </span>
              <div className={style.formRow}>
                <div className={style.formItem}>
                  {/* Tract creation requires reference locations with matching Salesforce LandGridTracts */}
                  <SurveyAreaAutocomplete
                    titleText="Survey Area*"
                    id={'tract-creation-location'}
                    placeholder=""
                    validateOnlyWhenDirty
                    onChange={({ selectedItem }) => {
                      setReferenceLocationField(selectedItem ?? undefined);
                    }}
                    location={referenceLocationField}
                    additionalPayload={{ requireSalesforceLandGridTract: true }}
                  />
                </div>
                <div className={style.formItem}>
                  <QuarterCalls
                    light={true}
                    labelText="Quarter Call"
                    placeholder="Quarter Call"
                    items={quarterCalls.value}
                    onRemove={(quarterCall) => {
                      setQuarterCalls((current) =>
                        current.filter((el) => el !== quarterCall)
                      );
                    }}
                    onAdd={(quarterCall) => {
                      setQuarterCalls((current) => [...current, quarterCall]);
                    }}
                    id={'tract-creation-quarter-calls'}
                  ></QuarterCalls>
                </div>
              </div>
              <div className={style.formRow}>
                <div className={style.formItem}>
                  <TextAreaField
                    light={true}
                    fieldAtom={form.tractFullLegalDescription}
                    labelText="Full Legal Description"
                  ></TextAreaField>
                </div>
              </div>
              <div className={style.formRow}>
                <div
                  className={classNames(style.formItem, style.lotsContainer)}
                >
                  <TextInput
                    light={true}
                    id="lots"
                    labelText="Lots"
                    value={lots.value.join(',')}
                    onChange={(e) => {
                      const lots = e.target.value.split(',');
                      setLots(lots);
                    }}
                  ></TextInput>
                  <div data-testid="tract-creation-lot-tags">
                    <LotTags />
                  </div>
                </div>
                <div className={style.gridException}>
                  <Checkbox
                    checked={gridException.value}
                    onChange={(_evt, { checked }: { checked: boolean }) => {
                      setGridException(checked);
                    }}
                    id="grid-exception"
                    labelText="Grid Exception"
                  ></Checkbox>
                </div>
              </div>
              {!isMapVisible && (
                <InlineNotification
                  hideCloseButton
                  lowContrast
                  kind="warning"
                  title=""
                  subtitle="Because Lots and/or Grid Exception is set, the Tract Record will be created in Insights. GIS will be notified to make the shape."
                ></InlineNotification>
              )}
              <div className={style.formRow}>
                <div className={style.formItem}>
                  <NumberField
                    light={true}
                    field={form.tractNetAcres}
                    id="tract-net-acres"
                    label="Tract Net Acres *"
                    thousandSeparator={true}
                    onChange={() => validateNetAcres()}
                  ></NumberField>
                </div>
                <div className={style.formItem}>
                  <NumberField
                    light={true}
                    field={form.grossAcres}
                    id="gross-acres"
                    label="Gross Acres*"
                    thousandSeparator={true}
                    onChange={() => validateNetAcres()}
                  ></NumberField>
                </div>
              </div>
              <div className={style.formRow}>
                <div className={style.formItem}>
                  <DropdownField
                    data-testid="tract-creation-net-acreage-units"
                    light={true}
                    field={form.netAcreageUnits}
                    id="net-acreage-units"
                    label="Choose an option"
                    items={netAcreageUnitsItems}
                    titleText="Net Acreage Units*"
                    onChange={() => validateNetAcres()}
                  ></DropdownField>
                </div>
              </div>
              <div className={style.formRow}>
                <div className={style.formItem}>
                  <NumberField
                    light={true}
                    field={form.leaseRoyaltyRate}
                    id="lease-royalty-rate"
                    label="Lease Royalty Rate"
                    thousandSeparator={false}
                    suffix="%"
                    disabled={isFieldDisabled('leaseRoyaltyRate')}
                    helperText={fixedPercentHint(
                      allowed,
                      'leaseRoyaltyRate',
                      instrumentType.value
                    )}
                  ></NumberField>
                </div>
                <div className={style.formItem}>
                  <DropdownField
                    data-testid="tract-creation-royalty-type"
                    light={true}
                    field={form.leaseRoyaltyType}
                    id="royalty-type"
                    label="Choose an option"
                    items={royaltyTypeItems}
                    titleText="Royalty Type"
                    disabled={isFieldDisabled('leaseRoyaltyType')}
                  ></DropdownField>
                </div>
              </div>
              <div className={style.formRow}>
                <div className={style.formItem}>
                  <NumberField
                    light={true}
                    field={form.royaltyBurdens}
                    id="royalty-burdens"
                    label="Royalty Burdens"
                    thousandSeparator={false}
                    suffix="%"
                    disabled={isFieldDisabled('royaltyBurdens')}
                  ></NumberField>
                </div>
                <div className={style.formItem}>
                  <NumberField
                    light={true}
                    field={form.orriBurdens}
                    id="orri-burdens"
                    label="ORRI Burdens"
                    thousandSeparator={false}
                    suffix="%"
                    disabled={isFieldDisabled('orriBurdens')}
                  ></NumberField>
                </div>
              </div>

              <hr className={style.sectionDivider} />
              <InterestConversionSection
                data={interestConversionData}
                isLoading={isInterestCalculationLoading}
                instrumentType={instrumentType.value}
                enabled={isBundleOfSticksSupported}
              />
              <hr className={style.sectionDivider} />

              <div className={style.formRow}>
                <div className={style.formItem}>
                  <TextField
                    light={true}
                    fieldAtom={form.alternateAssetID}
                    id="alternate-asset-id"
                    labelText="Alt Asset ID"
                  ></TextField>
                </div>
              </div>
              <div className={style.formRow}>
                <div className={style.formItem}>
                  <DropdownField
                    data-testid="tract-creation-title-quality"
                    light={true}
                    field={form.titleQuality}
                    id="title-quality"
                    label="Choose an option"
                    items={titleQualityItems}
                    itemToString={titleQualityLabel}
                    titleText="Title Quality"
                  ></DropdownField>
                </div>
                <div className={style.formItem}>
                  <DropdownField
                    data-testid="tract-creation-title-risk-factor"
                    light={true}
                    field={form.titleRiskFactor}
                    id="title-risk-factor"
                    label="Choose an option"
                    items={titleRiskFactorItems}
                    itemToString={titleRiskFactorLabel}
                    titleText="Title Risk Factor"
                  ></DropdownField>
                </div>
              </div>
              <div className={style.formRow}>
                <div className={style.formItem}>
                  <TextAreaField
                    light={true}
                    fieldAtom={form.depthFullDescription}
                    id="depth-full-description"
                    labelText="Depth Full Description"
                  ></TextAreaField>
                </div>
              </div>
              <div className={style.formRow}>
                <div className={style.formItem}>
                  <NumberField
                    light={true}
                    field={form.startDepth}
                    id="start-depth"
                    label="Start Depth"
                    thousandSeparator={true}
                  ></NumberField>
                </div>
                <div className={style.formItem}>
                  <NumberField
                    light={true}
                    field={form.endDepth}
                    id="end-depth"
                    label="End Depth"
                    thousandSeparator={true}
                  ></NumberField>
                </div>
              </div>
              <div className={style.formRow}>
                <div className={style.formItem}>
                  <TextAreaField
                    light={true}
                    fieldAtom={form.notes}
                    id="notes"
                    labelText="Notes"
                  ></TextAreaField>
                </div>
              </div>
            </Form>
            {/* Links hang off a saved tract, so nothing renders until the
                Salesforce Id exists — create mode has no record to fetch. */}
            {tract?.id ? (
              <div className={style.relatedItems}>
                <RelatedItemsSection
                  host={{
                    basePath: `/crm/tracts/${tract.id}`,
                    tractId: tract.id,
                    notifications: tract.notifications,
                    vesting: {
                      acquisitionEffectiveDate: tract.acquisitionEffectiveDate,
                      referenceLocationId: tract.referenceLocation?.id,
                      referenceLocationName: tract.referenceLocation?.name,
                      buyerEntity: tract.buyerEntity,
                      county: tract.county,
                    },
                  }}
                  selectorPageContent=".crm-tract-wizard-shell"
                />
              </div>
            ) : null}
          </div>
        }
        right={
          !geographies.length || !isMapVisible ? (
            <EmptyState
              icon={NoMapSvg}
              headerText="No Survey Areas Selected"
              helperText="Please select a survey area to get started."
              className={style.emptyMessage}
            />
          ) : (
            <div className={style.map}>
              <SurveyAreaMap />
            </div>
          )
        }
      />
    </div>
  );
};

export const TractCreation = () => {
  const formLoadable = useAtomValue(crmTractFormLoadableAtom);

  if (formLoadable.state === 'loading') {
    return <InlineLoading description="Loading tract form..." />;
  }

  if (formLoadable.state === 'hasError') {
    return (
      <InlineNotification
        kind="error"
        title="Error"
        subtitle="Failed to load tract form"
        lowContrast
      />
    );
  }

  if (formLoadable.state !== 'hasData' || !formLoadable.data) {
    return null;
  }

  return <TractCreationContent form={formLoadable.data} />;
};
